import React from 'react';
import { LineChart, ShieldCheck, Sparkles, Workflow } from 'lucide-react';
import {
  FUNDGENIUS_HQ_PRODUCT_NAME,
  FUNDGENIUS_HQ_TAGLINE,
} from '../../utils/funding/constants';
import { getFundGeniusAppUrl, getFundGeniusIntakeUrl } from '../../utils/hostnamePortal';

const highlights = [
  {
    title: 'Funding intake to funded',
    description: 'Track clients from intake through readiness, match, submissions, and commissions.',
    icon: Workflow,
  },
  {
    title: 'AI funding advisor',
    description: 'Deterministic recommendations grounded in readiness, documents, and lender fit.',
    icon: Sparkles,
  },
  {
    title: 'Agency workspace',
    description: 'Secure, agency-scoped funding operations without mixing credit repair workflows.',
    icon: ShieldCheck,
  },
  {
    title: 'Pipeline visibility',
    description: 'Dashboards, reports, and stage tracking for brokers and funding teams.',
    icon: LineChart,
  },
];

const FundGeniusPublicLandingPage = () => {
  const appLoginUrl = getFundGeniusAppUrl('/login');
  const signupUrl = '/signup';
  const intakeUrl = getFundGeniusIntakeUrl('/');

  return (
    <div className="min-h-screen bg-[#07111f] text-white">
      <header className="border-b border-white/10 bg-[#07111f]/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 text-sm font-bold">
              FG
            </div>
            <div>
              <div className="text-lg font-semibold">{FUNDGENIUS_HQ_PRODUCT_NAME}</div>
              <div className="text-xs text-slate-400">Funding operations workspace</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={intakeUrl}
              className="hidden rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/5 sm:inline-flex"
            >
              Start Intake
            </a>
            <a
              href={appLoginUrl}
              className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/5"
            >
              App Login
            </a>
            <a
              href={signupUrl}
              className="rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400"
              data-testid="fundgenius-public-signup-cta"
            >
              Get started
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.18),transparent_35%)]" />
          <div className="relative mx-auto max-w-6xl px-6 py-20">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
              FundGenius HQ
            </p>
            <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
              The funding workspace for brokers and agencies
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">{FUNDGENIUS_HQ_TAGLINE}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={signupUrl}
                className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-slate-100"
                data-testid="fundgenius-hero-signup-cta"
              >
                Start free signup
              </a>
              <a
                href={appLoginUrl}
                className="rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white hover:bg-white/5"
              >
                Open App Portal
              </a>
              <a
                href={intakeUrl}
                className="rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white hover:bg-white/5"
              >
                Submit Funding Intake
              </a>
            </div>
            <p className="mt-4 text-sm text-slate-400">
              Public marketing site only. Authenticated workspace lives at app.fundgeniushq.com.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-20">
          <div className="grid gap-5 md:grid-cols-2">
            {highlights.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
              >
                <Icon className="mb-4 h-6 w-6 text-cyan-300" />
                <h2 className="text-lg font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default FundGeniusPublicLandingPage;
